import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { coldChain } from '@/lib/trackizy';
import { showToast } from '@/components/shared/Toast';

export default function CopyColdPointsDialog({ fromDeviceId, onClose, onDone }) {
  const { t } = useTranslation();
  const devices = useSelector((s) => s.devices.items);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(devices)
      .filter((d) => d.id !== fromDeviceId && (d.name ?? '').toLowerCase().includes(q))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [devices, fromDeviceId, query]);

  const toggle = (id) => setPicked((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function submit() {
    setBusy(true);
    try {
      const r = await coldChain.copyPoints({ fromDeviceId, toDeviceIds: [...picked] });
      showToast([
        t('coldChain.copy.done', 'Copied {{n}} cold points', { n: r.copied }),
        r.skipped.length ? t('coldChain.copy.skipped', '{{n}} devices skipped (full or not available)', { n: r.skipped.length }) : null,
      ].filter(Boolean).join(' · '));
      onDone?.();
      onClose();
    } catch (e) {
      showToast(e?.message || t('coldChain.errors.save', 'Could not save'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{t('coldChain.copy.title', 'Copy cold points')}</h3>
          <button type="button" onClick={onClose} aria-label={t('coldChain.form.cancel', 'Cancel')} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X size={16} /></button>
        </div>
        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">{t('coldChain.copy.hint', "Names, sensors and ranges are copied into each vehicle's free slots.")}</p>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('coldChain.copy.search', 'Search vehicles')}
          className="mb-2 block h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800" />
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {list.map((d) => (
            <li key={d.id}>
              <label className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                <input type="checkbox" checked={picked.has(d.id)} onChange={() => toggle(d.id)} />
                <bdi>{d.name}</bdi>
              </label>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={submit} disabled={busy || picked.size === 0}
            className="inline-flex items-center gap-1.5 rounded-xl bg-blue-500 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />}{t('coldChain.copy.submit', 'Copy')}
          </button>
        </div>
      </div>
    </div>
  );
}
